import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { XCircle, Percent } from "@phosphor-icons/react";

const KEY = "ve_exit_popup_shown_v1";
const DISCOUNT_CODE = "COMEBACK10";

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) === "1") return;
    const onMouseOut = (e) => {
      // top-edge exit intent (desktop) and short-time exit-out
      if (e.clientY <= 0 && !visible) setVisible(true);
    };
    // Mobile fallback — show after 60s of inactivity
    const t = setTimeout(() => setVisible(true), 60_000);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(t);
    };
  }, [visible]);

  useEffect(() => {
    if (visible) localStorage.setItem(KEY, "1");
  }, [visible]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email });
      setSubscribed(true);
      toast.success("Code applied — enjoy!");
    } catch {
      toast.error("Couldn't subscribe. Try again.");
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur p-4"
          data-testid="exit-intent-overlay"
          onClick={() => setVisible(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md border border-white/15 bg-[#0d0d0d] p-8"
          >
            <button
              onClick={() => setVisible(false)}
              className="absolute right-3 top-3 text-white/40 hover:text-white"
              data-testid="exit-intent-close"
            >
              <XCircle size={22} weight="duotone" />
            </button>
            <div className="flex items-center gap-3">
              <Percent size={22} weight="fill" className="text-[#FF5A1F]" />
              <div className="font-label text-xs text-[#FF5A1F]">/ WAIT!</div>
            </div>
            {!subscribed ? (
              <>
                <h2 className="mt-4 font-display text-3xl font-black tracking-tighter">
                  Take <span className="text-[#00FFC2]">10% off</span> before you go.
                </h2>
                <p className="mt-3 text-sm text-white/60">
                  Drop your email — we'll send you the code plus first dibs on new flavour drops.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <input
                    data-testid="exit-intent-email"
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#00FFC2]"
                  />
                  <button
                    data-testid="exit-intent-submit"
                    className="w-full bg-[#FF5A1F] py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
                  >
                    Reveal my 10% code
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="mt-4 font-display text-3xl font-black tracking-tighter">
                  Boom. Use <span className="text-[#FFC900]">{DISCOUNT_CODE}</span> at checkout.
                </h2>
                <p className="mt-3 text-sm text-white/60">10% off — valid for 24 hours.</p>
                <Link
                  to="/shop"
                  onClick={() => setVisible(false)}
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-[#00FFC2] px-6 py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
                >
                  Start shopping
                </Link>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
